import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import { SvgIcon } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Alert from '@mui/material/Alert';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { getDropRejectErrorCode, getDropFileErrorMessageByCode } from 'utils/Error';
import { useImportCubeDefinition, ImportCubeDefinitionResponse } from 'services/v1/SystemTenant/AROCube/CubeBuilderService';

// Custom Icon
import { ReactComponent as CustomIconTrash } from 'assets/icons/icon-trash.svg';

const ModalContentStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 550,
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
};

const buttonActionStyle = { width: '100%' };

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

interface ModalCubeDefinitionImportProps {
  visible?: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onImportSuccess: (data: ImportCubeDefinitionResponse) => void;
  subjectId?: string;
}

export function ModalCubeDefinitionImport(props: ModalCubeDefinitionImportProps) {
  const { t } = useTranslation();
  const { onImportSuccess, onCancel, onClose, visible = false, subjectId } = props;

  const {
    reset: resetImporting,
    mutate: importData,
    isLoading: isImporting,
    error: errorImporting,
    isError: isErrorImporting,
  } = useImportCubeDefinition(subjectId);

  const [fileBuffer, setFileBuffer] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportCubeDefinitionResponse | null>(null);

  const onDrop = useCallback((acceptedFiles) => {
    setFileError(null);
    if (acceptedFiles.length > 0) {
      setFileBuffer(acceptedFiles[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDropRejected = useCallback((rejectedFiles) => {
    setFileBuffer(null);
    setFileError(getDropFileErrorMessageByCode(getDropRejectErrorCode(rejectedFiles[0])));
  }, []);

  const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: false,
    maxFiles: 1,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
  });

  const resetAllState = () => {
    setFileBuffer(null);
    setImportResult(null);
    resetImporting();
    acceptedFiles.length = 0;
  };

  const handleCloseModal = () => {
    if (importResult) {
      onImportSuccess(importResult);
    }
    resetAllState();
    onClose();
  };

  const handleOnStartImport = () => {
    const formData = new FormData();
    formData.append('file', fileBuffer as Blob);
    importData(formData, {
      onSuccess: (response) => {
        const data = response.data;
        if (data.warnings && data.warnings.length > 0) {
          setImportResult(data);
        } else {
          resetAllState();
          onClose();
          onImportSuccess(data);
        }
      },
    });
  };

  const handleOnDeleteFile = () => {
    resetAllState();
  };

  return (
    <div>
      <Modal open={visible} onClose={handleCloseModal}>
        <Stack direction='column' sx={ModalContentStyle}>
          <Stack justifyContent='space-between' direction='row' spacing={2}>
            <Typography variant='body1' component='h2' color='#394798'>
              {t('COMMON.MODAL_IMPORT.TITLE')}
            </Typography>
            <IconButton onClick={handleCloseModal} component='label'>
              <CloseIcon sx={closeIconStyle} fontSize='small' />
            </IconButton>
          </Stack>

          {isErrorImporting && (
            <Stack sx={{ my: 1 }}>
              <Alert severity='error'>
                <Typography variant='body2' component='p'>
                  {errorImporting?.response?.data?.message || 'Import failed. Please check your file and try again.'}
                </Typography>
              </Alert>
            </Stack>
          )}

          {importResult && (
            <Stack spacing={1} sx={{ my: 1, maxHeight: 300, overflow: 'auto' }}>
              <Alert severity='success'>
                <Typography variant='body2'>{importResult.message}</Typography>
              </Alert>
              {importResult.warnings && importResult.warnings.length > 0 && (
                <Alert severity='warning'>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    The import succeeded with the following warnings:
                  </Typography>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                    {importResult.warnings.map((warning, index) => (
                      <li key={index}>
                        <Typography variant='body2'>{warning}</Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
            </Stack>
          )}

          {!importResult && (
            <>
              {!fileBuffer && (
                <Stack alignItems='center' sx={{ py: 2, bgcolor: '#F7F7F7', my: 1 }}>
                  <Typography variant='body2'>{t('COMMON.MODAL_IMPORT.INSTRUCTION_EXCEL')}</Typography>
                </Stack>
              )}

              {!isErrorImporting && (
                <Stack alignItems='center' sx={{ py: 1 }}>
                  {!fileBuffer && (
                    <>
                      <div {...getRootProps({ className: 'dropzone' })}>
                        <Button
                          endIcon={<AddCircleOutlineOutlinedIcon />}
                          variant='main-table-panel'
                          sx={{ width: '35%', mr: 1 }}
                        >
                          {t('COMMON.MODAL_IMPORT.BUTTON_UPLOAD')}
                        </Button>
                        <p>{t('COMMON.MODAL_IMPORT.UPLOAD_AREA_EXCEL')}</p>
                        <input {...getInputProps()} />
                      </div>
                      <Typography variant='body2' sx={{ color: '#BB4942', mt: 1 }}>
                        {fileError}
                      </Typography>
                    </>
                  )}
                  {fileBuffer && (
                    <div {...getRootProps({ className: 'dropzone' })}>
                      <img src='/images/excel-icon.png' alt='Excel File' style={{ width: '25px', height: 'auto' }} />
                      <p className='file-name'>{fileBuffer?.name}</p>
                      <IconButton size='small'>
                        <SvgIcon
                          fontSize='small'
                          sx={{ fill: '#42BB93' }}
                          inheritViewBox
                          component={CustomIconTrash}
                          onClick={handleOnDeleteFile}
                        />
                      </IconButton>
                    </div>
                  )}
                </Stack>
              )}
            </>
          )}

          <Divider sx={{ mb: 1 }} />
          {importResult ? (
            <Stack direction='row' justifyContent='center' sx={{ py: 1 }}>
              <Button variant='main-table-panel' sx={buttonActionStyle} onClick={handleCloseModal}>
                OK
              </Button>
            </Stack>
          ) : (
            <Stack direction='row' justifyContent='center' spacing={2} alignItems='center' sx={{ py: 1 }}>
              <Button disabled={isImporting} variant='main-table-panel-border' sx={buttonActionStyle} onClick={onCancel}>
                {t('COMMON.BUTTON.CANCEL')}
              </Button>
              <ButtonLoading
                loading={isImporting}
                disabled={!fileBuffer || isErrorImporting}
                variant='main-table-panel'
                onClick={handleOnStartImport}
                sx={buttonActionStyle}
              >
                {t('COMMON.MODAL_IMPORT.START_IMPORT')}
              </ButtonLoading>
            </Stack>
          )}
        </Stack>
      </Modal>
    </div>
  );
}
